import { ValidationError, array, mixed, object } from 'yup'
import {
  LOAD_ECCENTRICITY_MISSING_MESSAGE,
  getMissingLoadEccentricityAxes,
} from '@editor/components/CheckSettingsFormFields/LoadEccentricityFormFields/utils'
import type { DesignFormValues } from './types'
import { isTimberLikeSettings } from './utils'

/** Yup `this.path` for one `elements` row, e.g. `elements[0]` or `elements.0`. */
const isActiveDesignElementSchemaPath = (path: string | undefined): boolean => {
  if (!path) return false
  return /^elements(?:\[0\]|\.0)$/.test(path)
}

function isPresentFiniteNumber(v: unknown): boolean {
  if (v === null || v === undefined) return false
  return typeof v === 'number' && Number.isFinite(v)
}

function isPositiveDimension(v: unknown): boolean {
  return isPresentFiniteNumber(v) && (v as number) > 0
}

/**
 * Validates Material & Querschnitt fields for the active element slice.
 * Returns a German error message or null when valid.
 */
export function validateCrossSection(cs: unknown): string | null {
  // Bundled `elements[i]` slices may omit cross section; only validate when one is present.
  if (cs == null || typeof cs !== 'object') {
    return null
  }

  const crossSection = cs as CrossSectionTimberOrCLTOrSteel

  if (crossSection.kind === 'CLT') {
    return null
  }

  if (crossSection.kind === 'Timber') {
    const missing: string[] = []
    const shape = crossSection.shape
    if (shape?.kind_literal !== 'RectangularCS') {
      return 'Querschnitt ist unvollständig.'
    }
    const identifier = crossSection.material?.identifier?.trim()
    if (!identifier) missing.push('Material')
    if (!isPositiveDimension(shape.width)) missing.push('QS Breite')
    if (!isPositiveDimension(shape.height)) missing.push('QS Höhe')
    if (!crossSection.usage_class) missing.push('Nutzungsklasse')
    if (missing.length === 0) return null
    return missing.length === 1 ? `${missing[0]} fehlt.` : `Bitte angeben: ${missing.join(', ')}.`
  }

  if (crossSection.kind === 'Steel') {
    const missing: string[] = []
    const shape = crossSection.shape
    if (shape?.kind_literal !== 'SteelCS') {
      return 'Querschnitt ist unvollständig.'
    }
    const identifier = crossSection.material?.identifier?.trim()
    if (!identifier) missing.push('Material')
    if (!shape.profile?.trim()) missing.push('Stahlprofil')
    if (missing.length === 0) return null
    return missing.length === 1 ? `${missing[0]} fehlt.` : `Bitte angeben: ${missing.join(', ')}.`
  }

  return 'Querschnitt ist unvollständig.'
}

/**
 * Validates Geom. Auflager / Überstand for one timber support row (same rules as CSAndAP support config).
 * Returns a German error message or null when valid.
 */
export function validateTimberSupportConfigRow(row: SupportConfig): string | null {
  const area = row.area
  const overhang = row.overhang

  if (!area && !overhang) {
    return 'Mindestens eine der folgenden Angaben ist erforderlich: Auflagerfläche oder Überstand'
  }

  const lengthVal = area?.length
  const widthVal = area?.width
  const hasArea = area !== null && area !== undefined
  const completeArea =
    isPresentFiniteNumber(lengthVal) &&
    isPresentFiniteNumber(widthVal) &&
    (lengthVal as number) > 0 &&
    (widthVal as number) > 0

  const left = overhang?.left
  const right = overhang?.right
  const front = overhang?.front
  const back = overhang?.back
  const overhangSides = [left, right, front, back]
  const hasOverhang = overhang !== null && overhang !== undefined
  const completeOverhang =
    overhangSides.every(isPresentFiniteNumber) && overhangSides.every(v => (v as number) >= 0)

  if (hasArea && !completeArea) {
    return 'Auflagerfläche: Länge und Breite müssen größer als 0 sein.'
  }
  if (hasOverhang && !completeOverhang) {
    return 'Überstand: alle Seiten (l, r, v, h) müssen angegeben werden (≥ 0).'
  }

  const fc90k = row.f_c_90_k
  const hasFc90k = fc90k !== null && fc90k !== undefined
  const completeFc90k = isPresentFiniteNumber(fc90k) && (fc90k as number) > 0
  if (hasFc90k && !completeFc90k) {
    return 'f_c,90,k: Wert muss größer als 0 sein.'
  }

  return null
}

const designFormElementSchema = object({
  elementGuid: mixed(),
  memberCheckSettingsSelectedElement: mixed().nullable(),
  exported: mixed(),
  comments: mixed(),
  crossSection: mixed(),
  checksSelectedElement: mixed(),
  supportConfigsForChecks: mixed(),
})
  .test('support-config-rows', '', function (value) {
    if (value == null || typeof value !== 'object') return true

    const form = value as DesignFormValues
    const settings = form.memberCheckSettingsSelectedElement
    if (!isTimberLikeSettings(settings) || !settings.support_configs?.length) return true

    for (let i = 0; i < settings.support_configs.length; i++) {
      const msg = validateTimberSupportConfigRow(settings.support_configs[i])
      if (msg) {
        return this.createError({
          path: `${this.path}.memberCheckSettingsSelectedElement.support_configs[${i}]`,
          message: msg,
        })
      }
    }
    return true
  })
  .test('load-eccentricity-complete', '', function (value) {
    if (value == null || typeof value !== 'object') return true
    if (!isActiveDesignElementSchemaPath(this.path)) return true

    const missingAxes = getMissingLoadEccentricityAxes(
      (value as DesignFormValues).memberCheckSettingsSelectedElement,
    )
    if (!missingAxes.length) return true
    return new ValidationError(
      missingAxes.map(axis =>
        this.createError({
          path: `${this.path}.memberCheckSettingsSelectedElement.load_eccentricity.${axis}`,
          message: LOAD_ECCENTRICITY_MISSING_MESSAGE,
        }),
      ),
    )
  })
  .test('cross-section-complete', '', function (value) {
    if (value == null || typeof value !== 'object') return true
    if (!isActiveDesignElementSchemaPath(this.path)) return true

    const msg = validateCrossSection((value as DesignFormValues).crossSection)
    if (msg) {
      return this.createError({
        path: `${this.path}.crossSection`,
        message: msg,
      })
    }
    return true
  })

/** Yup schema for the Results2 Bemessung form: one object per element under `elements`. */
export const results2DesignFormValidationSchema = object({
  elements: array().of(designFormElementSchema).required(),
})
